/**
 * Query helper functions for video service
 */

import { getSupabaseClient } from '../../utils/supabaseClient';
import type { VideoSearchParams } from '../../types/video';

// Lazy-load Supabase client to avoid circular dependency
let supabaseInstance: any = null;
const getSupabase = () => {
  if (!supabaseInstance) {
    supabaseInstance = getSupabaseClient();
  }
  return supabaseInstance;
};

/**
 * Apply filters to video query
 */
export const applyVideoFilters = (query: any, params?: VideoSearchParams) => {
  let filteredQuery = query;

  if (params?.categories && params.categories.length > 0) {
    filteredQuery = filteredQuery.contains('category', params.categories);
  }

  if (params?.tags && params.tags.length > 0) {
    filteredQuery = filteredQuery.contains('tags', params.tags);
  }

  if (params?.duration_min) {
    filteredQuery = filteredQuery.gte('duration_seconds', params.duration_min);
  }

  if (params?.duration_max) {
    filteredQuery = filteredQuery.lte('duration_seconds', params.duration_max);
  }

  return filteredQuery;
};

/**
 * Apply sorting to video query
 */
export const applyVideoSorting = (query: any, sortBy?: string) => {
  switch (sortBy) {
    case 'newest':
      return query.order('published_at', { ascending: false });
    case 'oldest':
      return query.order('published_at', { ascending: true });
    case 'most_viewed':
      return query.order('view_count', { ascending: false });
    case 'title':
      return query.order('title', { ascending: true });
    default:
      return query.order('added_at', { ascending: false });
  }
};

/**
 * Apply pagination to video query
 */
export const applyVideoPagination = (query: any, params?: VideoSearchParams) => {
  let paginatedQuery = query;

  if (params?.limit) {
    paginatedQuery = paginatedQuery.limit(params.limit);
  }

  if (params?.offset) {
    const limit = params.limit || 10;
    paginatedQuery = paginatedQuery.range(params.offset, params.offset + limit - 1);
  }

  return paginatedQuery;
};

/**
 * Build search query for videos
 */
export const buildSearchQuery = (searchTerm: string) => {
  return `title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,channel_title.ilike.%${searchTerm}%`;
};